import * as React from 'react'
import clsx from 'clsx'
import * as plume from '@ulule/owl-kit-components/next'

import { Container } from '../../_shared/Container'
import { SectionHead, SectionHeadHighlight } from '../../_shared/SectionHead'
import { Trustpilot } from '../../_shared/Trustpilot'
import { type UluleStats } from '../../_shared/ululeStats'
import { CountUp } from '../components/CountUp'
import { SUCCESS, type SuccessRow } from '../content'
import { FIGURES } from '../figures'
import { useStatsTracking } from '../tracking'
import styles from './Success.module.css'

function formatPercent(value: number): string {
  return String(Math.round(value))
}

/**
 * The success rate is live, from the feed's most recent representative year. Overshoot and
 * share-driven have no field in the feed, so they come from `figures.json`.
 */
function makeRows(stats: UluleStats | null): SuccessRow[] {
  const successRate =
    stats?.currentYearSuccessPercent === undefined
      ? []
      : [
          {
            value: stats.currentYearSuccessPercent,
            suffix: '%',
            title: SUCCESS.titles.successRate,
            body: SUCCESS.bodies.successRate,
            // Tones travel with the row rather than its index: the first row is the only optional
            // one, so a positional lookup would recolour the rest when the feed omits it.
            tone: styles.rowOne,
          },
        ]

  return [
    ...successRate,
    {
      value: FIGURES.overshootPercent,
      suffix: '%',
      title: SUCCESS.titles.overshoot,
      body: SUCCESS.bodies.overshoot,
      tone: styles.rowTwo,
    },
    {
      value: FIGURES.shareDrivenPercent,
      suffix: '%',
      prefix: '+',
      title: SUCCESS.titles.shareDriven,
      body: SUCCESS.bodies.shareDriven,
      tone: styles.rowThree,
    },
  ]
}

export function Success({ stats }: { stats: UluleStats | null }): React.ReactElement {
  const tracking = useStatsTracking()
  const rows = makeRows(stats)

  return (
    <section className={styles.success} id="succes">
      <Container>
        <SectionHead
          size="wide"
          kicker={SUCCESS.kicker}
          title={
            <>
              {'Une plateforme et une équipe au service de '}
              <SectionHeadHighlight>votre succès&nbsp;!</SectionHeadHighlight>
            </>
          }
        />
        <div className={styles.rows}>
          {rows.map((row) => (
            <article key={row.title} className={styles.row}>
              <div className={styles.row__fig}>
                <div className={clsx(styles.row__num, row.tone)}>
                  {row.prefix}
                  <CountUp value={row.value} suffix={row.suffix} format={formatPercent} />
                </div>
              </div>
              <div className={styles.row__body}>
                <h3 className={styles.row__title}>{row.title}</h3>
                {/* Speech bubble: the tail is drawn with two stacked triangles, ink then fill. */}
                <div className={styles.bulle}>
                  <p>{row.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
        <div className={styles.rows__cta}>
          <plume.RoundButton
            as="a"
            href={SUCCESS.cta.href}
            color="black"
            size="big"
            onClick={() => tracking.ctaClick('success')}
          >
            {SUCCESS.cta.label}
          </plume.RoundButton>
          <Trustpilot />
        </div>
      </Container>
    </section>
  )
}
